import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as backend from 'src/backend';
import useCheckCompletedOnboarding, {
  getRecipientOnboardingState,
  shouldValidateDonorOnboarding,
} from 'src/hooks/useCheckCompletedOnboarding';
import useAuth from 'src/hooks/useAuth';
import {
  CheckboxFormComponent,
  checkboxFormOnChange,
  DateComponent,
  dateOnChange,
  dateTimeOnChange,
  makeMultiline,
  SelectComponent,
  AutoCompleteComponent,
  ToggleButtonComponent,
  RadioGroupComponent,
  TimeComponent,
  timeOnChange,
  foodCategoryOnChange,
  SelectAddressComponentPopup,
  selectAddressOnChange,
  processFoodTypes,
  requireAtLeastOneFoodType,
  foodTypeWeightSchema,
  buildDonationWriteFields,
  DayOfTheMonthSelector,
  UploadImagesComponent,
  uploadImagesOnChange,
} from 'src/hooks/useForm';
import moment from 'moment-timezone';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { FOOD_TYPES, useGetVehicleLabels } from 'src/constants';
import { useMeasureFieldUnits } from 'src/components/MeasureField';
import { processRecurringIntervalData } from 'src/utils/recurring';
import _isFinite from 'lodash/isFinite';
import { roundMinutes, getMinEndTime } from 'src/utils/time';
import {
  getVehicle,
  useDonationFoodTypeSchema,
  useMultipleDriversSchema,
} from 'src/hooks/useCreateDonation';
import FoodTypeSelector from 'src/utils/foodTypeSelector';
import useDaysOfTheWeek from 'src/hooks/useDaysOfTheWeek';
import { validateEndTimeAfterStartTime } from 'careit';
import Driver from 'src/components/Driver';

export const useGetFormConfig = (
  donors,
  recipients,
  timezoneId = 'America/Los_Angeles',
  orgsRefresh,
  preferredUnit = 'lbs',
  vehicles,
  drivers,
  user = {}
) => {
  timezoneId = timezoneId ?? 'America/Los_Angeles';
  const { t } = useTranslation('translation');
  const daysOfTheWeek = useDaysOfTheWeek();
  const vehicleLabelsWithUnit = useGetVehicleLabels();
  const { plan, isFoodMover } = useAuth();

  return [
    {
      section: 'stepper',
      vertical: true,
      steps: [
        {
          title: t('donations.food-run-details'),
          items: [
            { id: 'title', name: t('donations.title') },
            {
              id: 'description',
              name: t('donations.description'),
              params: makeMultiline(8, 1000),
            },
            {
              id: 'foodTypes',
              name: t('donations.food-types'),
              component: (params) => <FoodTypeSelector showTitle {...params} />,
              onChange: foodCategoryOnChange,
              params: { color: 'primary' },
            },
            {
              id: 'vehicleSize',
              name: t('donations.recommended-vehicle'),
              component: RadioGroupComponent(vehicleLabelsWithUnit),
              params: {
                row: true,
              },
            },
          ],
        },
        {
          title: t('donations.time-and-assignment'),
          items: [
            {
              section: 'horizontal',
              collapseSize: 'xs',
              items: [
                {
                  id: 'occurrenceType',
                  name: t('donations.occurrence-type'),
                  component: RadioGroupComponent([
                    { label: t('donations.one-time'), value: 'onetime' },
                    { label: t('donations.recurring'), value: 'recurring' },
                    { label: t('donations.indefinite'), value: 'indefinite' },
                  ]),
                  defaultState: 'onetime',
                  mapUpdate: ({ state, id, updates }) => ({
                    ...state,
                    [id]: updates,
                    available_on_unassign:
                      updates === 'onetime'
                        ? false
                        : state.occurrenceType === 'onetime'
                          ? Boolean(user?.available_on_unassign_default ?? true)
                          : state.available_on_unassign,
                  }),
                  params: {
                    fullWidth: true,
                  },
                },
                {
                  id: 'interval',
                  name: t('donations.repeat-every'),
                  condition: (state) =>
                    ['recurring', 'indefinite'].includes(state.occurrenceType),
                  params: {
                    items: [
                      { value: 'Daily', title: t('donations.daily') },
                      { value: 'Weekly', title: t('donations.weekly') },
                      { value: 'Bi-weekly', title: t('donations.bi-weekly') },
                      { value: 'Monthly', title: t('donations.monthly') },
                    ],
                  },
                  component: SelectComponent,
                  defaultState: 'Daily',
                },
              ],
            },
            {
              id: 'dayOfTheMonth',
              name: t('donations.repeat-on'),
              condition: (state) =>
                ['recurring', 'indefinite'].includes(state.occurrenceType) &&
                state.interval === 'Monthly',
              defaultState: {
                isLastDay: false,
                customDay: null,
                week: {
                  number: null,
                  dayOfTheWeek: null,
                },
              },
              component: DayOfTheMonthSelector,
              onChange: dateOnChange,
            },
            {
              section: 'horizontal',
              name: 'dayOfTheWeek',
              id: 'dayOfTheWeek',
              condition: (state) =>
                ['recurring', 'indefinite'].includes(state.occurrenceType) &&
                (state.interval === 'Weekly' || state.interval === 'Bi-weekly'),
              compact: true,
              left: true,
              title: t('donations.repeat-on'),
              items: [...'SMTWTFS'].map((_, i) => ({
                id: `repeat-${i}`,
                name: daysOfTheWeek[i],
                params: { sx: { margin: '5px' } },
                component: ToggleButtonComponent,
                onChange: checkboxFormOnChange,
              })),
            },
            {
              id: 'startDate',
              name: t('donations.donation-available-date'),
              condition: (state) => state.occurrenceType === 'onetime',
              defaultState: new Date(
                moment().tz(timezoneId).format('YYYY-MM-DD HH:mm:ss')
              ),
              component: DateComponent,
              onChange: dateTimeOnChange,
            },
            {
              section: 'horizontal',
              collapseSize: 'xs',
              condition: (state) => state.occurrenceType === 'onetime',
              items: [
                {
                  id: 'startTime',
                  name: t('donations.start-time'),
                  defaultState: new Date(
                    roundMinutes(moment())
                      .tz(timezoneId)
                      .format('YYYY-MM-DD HH:mm:ss')
                  ),
                  component: TimeComponent,
                  onChange: timeOnChange,
                  params: { fullWidth: false, sx: { width: 260 } },
                },
                {
                  id: 'endTime',
                  name: t('donations.end-time'),
                  defaultState: new Date(
                    roundMinutes(moment())
                      .tz(timezoneId)
                      .add(5, 'hours')
                      .format('YYYY-MM-DD HH:mm:ss')
                  ),
                  component: TimeComponent,
                  onChange: timeOnChange,
                  mapState: (state) => ({
                    minTime: getMinEndTime(state),
                  }),
                  params: { fullWidth: false, sx: { width: 260 } },
                },
              ],
            },
            {
              section: 'horizontal',
              title: t('donations.recurring-interval'),
              name: t('donations.recurring-interval'),
              collapseSize: 'xs',
              condition: (state) =>
                ['recurring', 'indefinite'].includes(state.occurrenceType),
              items: [
                {
                  id: 'startDate',
                  name: t('donations.start-date'),
                  defaultState: new Date(
                    moment().tz(timezoneId).format('YYYY-MM-DD HH:mm:ss')
                  ),
                  component: DateComponent,
                  onChange: dateTimeOnChange,
                  mapState: () => ({
                    minDate: new Date(
                      moment().tz(timezoneId).format('YYYY-MM-DD HH:mm:ss')
                    ),
                  }),
                },
                {
                  id: 'endDate',
                  name: t('donations.end-date'),
                  condition: (state) => state.occurrenceType !== 'indefinite',
                  defaultState: new Date(
                    moment()
                      .tz(timezoneId)
                      .add(1, 'days')
                      .format('YYYY-MM-DD HH:mm:ss')
                  ),
                  component: DateComponent,
                  onChange: dateTimeOnChange,
                  mapState: (state) => ({
                    minDate: state.startDate,
                  }),
                },
                {
                  id: 'startTime',
                  name: t('donations.start-time'),
                  defaultState: new Date(
                    roundMinutes(moment())
                      .tz(timezoneId)
                      .format('YYYY-MM-DD HH:mm:ss')
                  ),
                  component: TimeComponent,
                  onChange: timeOnChange,
                },
                {
                  id: 'endTime',
                  name: t('donations.end-time'),
                  defaultState: new Date(
                    moment()
                      .tz(timezoneId)
                      .add(1, 'days')
                      .add(5, 'hours')
                      .format('YYYY-MM-DD HH:mm:ss')
                  ),
                  component: TimeComponent,
                  onChange: timeOnChange,
                  mapState: (state) => ({
                    minTime: getMinEndTime(state),
                  }),
                },
              ],
            },
            {
              id: 'donor',
              name: t('donations.assign-donor'),
              params: {
                className: 'full-width',
                fullWidth: false,
                sx: { width: 'calc(100% - 200px)' },
                onRefresh: orgsRefresh,
                displayRefresh: true,
                items: [
                  {
                    value: 'unassigned',
                    title: t('donations.unassigned'),
                  },
                  ...donors.map((donor, index) => ({
                    value: donor.team_id,
                    title: donor.name,
                    formatted_address: donor.formatted_address,
                    key: `donor-${donor.team_id}-${index}`,
                  })),
                ],
              },
              component: AutoCompleteComponent,
              defaultState: 'unassigned',
            },
            {
              id: 'receiver',
              name: t('donations.assign-receiver'),
              condition: (state) => state.donor !== 'unassigned',
              params: {
                className: 'full-width',
                fullWidth: false,
                sx: { width: 'calc(100% - 200px)' },
                onRefresh: orgsRefresh,
                displayRefresh: true,
                items: [
                  {
                    value: 'unassigned',
                    title: t('donations.unassigned'),
                  },
                  ...recipients.map((receipient, index) => ({
                    value: receipient.team_id,
                    title: receipient.name,
                    formatted_address: receipient.formatted_address,
                    animal_food_rescuer_type:
                      receipient.animal_food_rescuer_type,
                    key: `recipient-${receipient.team_id}-${index}`,
                  })),
                ],
              },
              component: AutoCompleteComponent,
              defaultState: 'unassigned',
            },
            {
              id: 'custom_address',
              name: t('donations.use-custom-address'),
              params: { width: '200px' },
              condition: (state) => state.donor !== 'unassigned',
              component: CheckboxFormComponent,
              onChange: checkboxFormOnChange,
            },
            {
              section: 'horizontal',
              collapseSize: 'xs',
              condition: (state) => state.custom_address,
              items: [
                {
                  id: 'pickup_address',
                  name: t('donations.custom-pickup-address'),
                  component: SelectAddressComponentPopup,
                  onChange: selectAddressOnChange,
                },
                {
                  id: 'dest_address',
                  name: t('donations.custom-destination-address'),
                  component: SelectAddressComponentPopup,
                  onChange: selectAddressOnChange,
                },
              ],
            },
            {
              id: 'vehicle',
              name: t('donations.vehicle'),
              component: SelectComponent,
              defaultState: null,
              params: {
                hasClear: true,
                className: 'full-width',
                fullWidth: false,
                sx: { width: 'calc(100% - 200px)' },
                autocomplete: true,
                items: getVehicle(vehicles, preferredUnit),
              },
              condition: () => vehicles.length,
            },
            {
              id: 'driver',
              name: t('donations.runner'),
              params: {
                className: 'full-width',
                fullWidth: false,
                sx: { width: 'calc(100% - 200px)' },
                autocomplete: true,
                items: [
                  {
                    value: 'unassigned',
                    title: (
                      <Typography color="text.secondary">
                        {t('donations.unassigned')}
                      </Typography>
                    ),
                  },
                  ...drivers
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((teamMember, index) => ({
                      value: teamMember.id,
                      title: <Driver user={teamMember} />,
                      key: `driver-${teamMember.id}-${index}`,
                    })),
                ],
              },
              component: SelectComponent,
              defaultState: 'unassigned',
            },
            {
              id: 'available_on_unassign',
              name: t('donations.make-available-on-unassign'),
              condition: (state) =>
                ![null, undefined, 'unassigned'].includes(state.driver),
              defaultState: false,
              params: { width: '200px' },
              component: CheckboxFormComponent,
              onChange: checkboxFormOnChange,
            },
            {
              id: 'estimatedRescueTimeMinutes',
              name: t('donations.estimated-rescue-time-minutes'),
              defaultState: user?.rescue_time_tracking_enabled
                ? user?.default_rescue_time_minutes || 30
                : 30,
              params: {
                type: 'number',
                inputProps: { min: 1, max: 720 },
                helperText: t(
                  'donations.estimated-rescue-time-minutes-description'
                ),
              },
              condition: () =>
                user?.rescue_time_tracking_enabled &&
                (plan === 'basic' || isFoodMover),
            },
          ],
        },
        {
          title: t('donations.instructions-and-optional-images'),
          items: [
            {
              id: 'images',
              name: t('donations.images'),
              condition: (state) => state.occurrenceType === 'onetime',
              defaultState: [],
              component: UploadImagesComponent,
              onChange: uploadImagesOnChange,
              params: { displayLabel: true },
            },
            {
              id: 'recurringImages',
              name: t('donations.images-this-images-will-be-shown-on-all-runs'),
              condition: (state) => state.occurrenceType !== 'onetime',
              defaultState: [],
              component: UploadImagesComponent,
              onChange: uploadImagesOnChange,
              params: { displayLabel: true },
            },
            {
              id: 'pickupInstructions',
              name: t('donations.pickup-instructions'),
              params: makeMultiline(8, 1000),
            },
            {
              id: 'dropoffInstructions',
              name: t('donations.dropoff-instructions'),
              params: makeMultiline(8, 1000),
            },
            {
              id: 'notifyOnEdit',
              name: t('donations.notify-if-changes-made-on-edit'),
              params: { width: '200px' },
              component: CheckboxFormComponent,
              onChange: checkboxFormOnChange,
            },
            {
              id: 'markAsIncomplete',
              name: t('donations.mark-past-run-as-incomplete'),
              condition: (state) =>
                state.occurrenceType === 'onetime' &&
                moment(
                  moment(state.startDate).format('YYYY-MM-DD') +
                    ' ' +
                    moment(state.endTime).format('HH:mm:00')
                ).isBefore(moment()),
              defaultState: false,
              params: { width: '200px' },
              component: CheckboxFormComponent,
              onChange: checkboxFormOnChange,
            },
          ],
        },
      ],
    },
  ];
};

export const useAddFoodRunSchema = () => {
  const { user } = useAuth();
  const { t } = useTranslation('translation');
  const multipleDriversSchema = useMultipleDriversSchema();
  const checkCompletedOnboarding = useCheckCompletedOnboarding();
  const { WEIGHT_UNITS } = useMeasureFieldUnits();
  return (
    donors,
    recipients,
    {
      recipientStatusByTeamId = {},
      allowOpenRecipientSelection = false,
      existingDonorTeamId = null,
    } = {}
  ) =>
    Yup.object({
      description: Yup.string().required(t('donations.description-required')),
      foodTypes: Yup.object({
        ...FOOD_TYPES.map(({ value }) => value.toLowerCase()).reduce(
          (foodTypes, foodType) => ({
            ...foodTypes,
            [foodType]: Yup.object({
              value: foodTypeWeightSchema(t),
              unit: Yup.string().oneOf(WEIGHT_UNITS.map(({ value }) => value)),
            }),
          }),
          {}
        ),
      }).test(
        'at-least-one-property',
        t('donations.at-least-one-food-type'),
        (value) => Object.entries(processFoodTypes(value)).length > 0
      ),
      images: Yup.array().optional(),
      occurrenceType: Yup.string()
        .oneOf(['onetime', 'recurring', 'indefinite'])
        .required(t('donations.select-a-occurrence-type')),
      interval: Yup.string().when('occurrenceType', {
        is: (occurrenceType) =>
          ['recurring', 'indefinite'].includes(occurrenceType),
        then: () =>
          Yup.string().required(t('donations.select-a-recurring-interval')),
      }),
      startDate: Yup.string().required(t('donations.select-start-date')),
      endDate: Yup.string()
        .when(['occurrenceType'], {
          is: 'recurring',
          then: () =>
            Yup.string()
              .required(t('donations.select-end-date'))
              .test(
                'is-after-start-date',
                t('donations.end-date-must-be-after-start-date'),
                (value, context) => {
                  return (
                    value &&
                    context.parent.startDate &&
                    moment(value)
                      .startOf('day')
                      .isAfter(moment(context.parent.startDate).startOf('day'))
                  );
                }
              ),
        })
        .when(['occurrenceType'], {
          is: 'indefinite',
          then: () => Yup.string().nullable(),
        }),
      startTime: Yup.date().required(t('donations.select-a-start-time')),
      endTime: Yup.date()
        .required(t('donations.select-a-end-time'))
        .test(
          'is-after-start-time',
          t('donations.end-time-must-be-after-start-time'),
          validateEndTimeAfterStartTime
        ),
      daysOfTheWeek: Yup.array().when(['occurrenceType', 'interval'], {
        is: (occurrenceType, interval) =>
          ['recurring', 'indefinite'].includes(occurrenceType) &&
          ['Weekly', 'Bi-weekly'].includes(interval),
        then: () =>
          Yup.array().min(1, t('donations.select-at-least-one-day')).required(),
      }),
      dayOfTheMonth: Yup.object({}).when(['occurrenceType', 'interval'], {
        is: (occurrenceType, interval) =>
          ['indefinite', 'recurring'].includes(occurrenceType) &&
          interval === 'Monthly',
        then: () =>
          Yup.object()
            .shape({
              isLastDay: Yup.bool(),
              customDay: Yup.number().nullable(),
              week: Yup.object()
                .shape({
                  number: Yup.number().nullable(),
                  dayOfTheWeek: Yup.number().nullable(),
                })
                .nullable(),
            })
            .test(
              'atLeastOneNonNull',
              t('donations.select-day-of-the-month'),
              function (value) {
                const { isLastDay, customDay, week } = value;
                return (
                  isLastDay ||
                  customDay ||
                  (_isFinite(week.number) && _isFinite(week.dayOfTheWeek))
                );
              }
            )
            .required(t('donations.select-day-of-the-month')),
        otherwise: () => Yup.object().optional(),
      }),
      donor: Yup.string()
        .notOneOf(['unassigned'], t('donations.select-a-value'))
        .test(
          'completed-onboarding',
          ({ value: donor }) =>
            t('donations.you-must-complete-the-required-onboarding-items', {
              name: donors.find((team) => team.team_id === donor)?.name,
            }),
          async (donor, context) => {
            if (
              !shouldValidateDonorOnboarding({
                donorTeamId: donor,
                existingDonorTeamId,
                isFoodRun: context.parent.isFoodRun,
              })
            ) {
              return true;
            }

            const { requiredItems = [] } =
              await backend.team.getItemsRequiredToRescueFrom(donor);
            return getRecipientOnboardingState({ requiredItems }).isAssignable;
          }
        )
        .required(t('donations.select-a-donor')),
      drivers: Yup.array().when(
        ['occurrenceType', 'startDate', 'endTime', 'markAsIncomplete'],
        {
          is: (occurrenceType, startDate, endTime, markAsIncomplete) => {
            let endTimeMoment = moment(endTime);
            let startDateAndEndTime = moment(startDate)
              .hours(endTimeMoment.hours())
              .minutes(endTimeMoment.minutes());
            return (
              occurrenceType === 'onetime' &&
              !markAsIncomplete &&
              startDateAndEndTime.isBefore(moment())
            );
          },
          then: () => multipleDriversSchema,
          otherwise: () => Yup.array().nullable().notRequired(),
        }
      ),
      receiver: (allowOpenRecipientSelection
        ? Yup.string().optional()
        : Yup.string()
            .notOneOf(['unassigned'], t('donations.select-a-receiver'))
            .required(t('donations.select-a-receiver'))
      ).test(
        'completed-onboarding',
        ({ value: receiver }) =>
          t('donations.must-complete-onboarding-items', {
            name:
              recipients.find((team) => team.team_id === receiver)?.name ?? '',
          }),
        (receiver) =>
          checkCompletedOnboarding.validate(
            receiver,
            recipientStatusByTeamId?.[receiver]
          )
      ),
      vehicleSize: Yup.string().required(
        t('donations.select-recommended-vehicle-size')
      ),
      estimatedRescueTimeMinutes: Yup.number()
        .typeError(t('form.validation.number'))
        .min(1, t('donations.estimated-rescue-time-minimum'))
        .max(720, t('donations.estimated-rescue-time-maximum'))
        .integer(t('donations.estimated-rescue-time-integer'))
        .when(['isFoodRun', 'deliveryOption'], {
          is: (isFoodRun, deliveryOption) =>
            user.rescue_time_tracking_enabled &&
            (Boolean(isFoodRun) ||
              deliveryOption === 0 ||
              deliveryOption === '0'),
          then: (x) =>
            x.required(t('donations.estimated-rescue-time-required')),
          otherwise: (x) => x.optional().nullable(),
        })
        .nullable(),
    });
};

export const useEditRecurringDonationSchema = () => {
  const { t } = useTranslation('translation');
  const { user } = useAuth();
  const checkCompletedOnboarding = useCheckCompletedOnboarding();
  const multipleDriversSchema = useMultipleDriversSchema();
  const foodTypesSchema = useDonationFoodTypeSchema();
  const AddFoodRunSchema = useAddFoodRunSchema();
  return (donors, recipients, { recipientStatusByTeamId = {} } = {}) =>
    AddFoodRunSchema(donors, recipients, { recipientStatusByTeamId }).shape({
      isPartialRescue: Yup.boolean(),
      foodTypes: Yup.object().when('isPartialRescue', {
        is: true,
        then: () =>
          requireAtLeastOneFoodType(
            foodTypesSchema({
              isPartialRescue: true,
              nearbyNPOs: recipients,
              recipientStatusByTeamId,
            }),
            t('donations.at-least-one-food-type')
          ),
        otherwise: () =>
          requireAtLeastOneFoodType(
            foodTypesSchema({
              isPartialRescue: false,
              nearbyNPOs: recipients,
              recipientStatusByTeamId,
            }),
            t('donations.at-least-one-food-type')
          ),
      }),

      title: Yup.string()
        .test(
          'no-email',
          t('form.validation.email-not-allowed'),
          function (value) {
            if (!value) return true;

            // This regex pattern looks for common email patterns
            const emailPattern =
              /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
            return !emailPattern.test(value);
          }
        )
        .required(t('donations.donation-title-required')),
      deliveryOption: Yup.string().required(
        t('donations.select-a-delivery-option')
      ),
      receiver: Yup.string()
        .when(['isFoodRun', 'deliveryOption'], {
          is: (isFoodRun, deliveryOption) =>
            isFoodRun && Number(deliveryOption) !== 4,
          then: (schema) =>
            schema
              .notOneOf(['unassigned'])
              .required(t('donations.select-a-receiver')),
          otherwise: (schema) => schema.optional().nullable(),
        })
        .test(
          'completed-onboarding',
          ({ value: receiver }) =>
            checkCompletedOnboarding.buildErrorMessage(recipients, receiver),
          (receiver) =>
            checkCompletedOnboarding.validate(
              receiver,
              recipientStatusByTeamId?.[receiver]
            )
        ),
      dayOfTheMonth: Yup.object({}).when(['occurrenceType', 'interval'], {
        is: (occurrenceType, interval) =>
          occurrenceType === 'recurring' && interval === 'Monthly',
        then: () =>
          Yup.object()
            .shape({
              isLastDay: Yup.bool(),
              customDay: Yup.string().nullable(),
              week: Yup.object()
                .shape({
                  number: Yup.number().nullable(),
                  dayOfTheWeek: Yup.number().nullable(),
                })
                .nullable(),
            })
            .test(
              'atLeastOneNonNull',
              t('donations.select-day-of-the-month'),
              function (value) {
                const { isLastDay, customDay, week } = value;
                return (
                  isLastDay ||
                  customDay ||
                  (_isFinite(week.number) && _isFinite(week.dayOfTheWeek))
                );
              }
            )
            .required(t('donations.select-day-of-the-month')),
        otherwise: () => Yup.object().optional(),
      }),
      drivers: Yup.array().when(
        ['deliveryOption', 'receiver', 'isFoodRun', 'isPartialRescue'],
        {
          is: (deliveryOption, receiver, isFoodRun, isPartialRescue) =>
            Number(deliveryOption) === 0 &&
            !isFoodRun &&
            (isPartialRescue || receiver),
          then: () => multipleDriversSchema,
          otherwise: () => Yup.array().nullable().notRequired(),
        }
      ),
      driver: Yup.string().optional().nullable(),
      dest_address: Yup.object({}).when(['deliveryOption'], {
        is: (deliveryOption) => Number(deliveryOption) === 0,
        then: (schema) =>
          schema.required(
            t('donations.donation-destination-location-required')
          ),
        otherwise: (schema) => schema.optional(),
      }),
      pickup_address: Yup.object({}).when(['deliveryOption'], {
        is: (deliveryOption) => Number(deliveryOption) === 0,
        then: (schema) =>
          schema.required(t('donations.pickup-location-required')),
        otherwise: (schema) => schema.optional(),
      }),
      vehicleSize: Yup.string().when(['deliveryOption'], {
        is: (deliveryOption) => Number(deliveryOption) !== 4,
        then: (schema) =>
          schema.required(t('donations.select-recommended-vehicle-size')),
        otherwise: (schema) => schema.optional(),
      }),
      estimatedRescueTimeMinutes: Yup.number()
        .typeError(t('form.validation.number'))
        .min(1, t('donations.estimated-rescue-time-minimum'))
        .max(720, t('donations.estimated-rescue-time-maximum'))
        .integer(t('donations.estimated-rescue-time-integer'))
        .nullable()
        .when(['deliveryOption'], {
          is: (deliveryOption) =>
            Number(deliveryOption) !== 4 && user.rescue_time_tracking_enabled,
          then: (schema) =>
            schema.required(t('donations.estimated-rescue-time-required')),
        }),
    });
};

export const processFoodRunFormData = (data) => {
  const {
    occurrenceType,
    isRecurring,
    interval,
    daysOfTheWeek,
    startDate,
    endDate,
    startTime,
    endTime,
    isDST,
  } = processRecurringIntervalData(data);

  const newEndDate = isRecurring ? endDate : startDate; // End date same as start date for one time runs

  const isPastDate = moment(
    moment(newEndDate).format('YYYY-MM-DD') +
      ' ' +
      moment(data.endTime).format('HH:mm:00')
  ).isBefore(moment());

  const isCompleted =
    occurrenceType === 'onetime' && isPastDate && !data.markAsIncomplete;

  const newFoodRun = {
    isRecurring,
    interval,
    dayOfTheMonth: data.dayOfTheMonth,
    daysOfTheWeek,
    startDate,
    endDate: occurrenceType === 'indefinite' ? null : newEndDate,
    startTime,
    endTime,
    isDST,
    isPastDate,
    vehicleSize: Number(data.vehicleSize),
    vehicle: data.vehicle,
    title: data.title?.trim() ? data.title : null,
    description: data.description,
    donationId: data.donationId ?? uuidv4(),
    patternId: data.patternId ?? uuidv4(),
    donorTeam: data.donor,
    driverId: data.driver === 'unassigned' ? null : data.driver,
    drivers: (data.drivers || []).filter((d) => !!d.user_id),
    availableOnUnassign: Boolean(data.available_on_unassign),
    pickupInstructions: data.pickupInstructions,
    dropoffInstructions: data.dropoffInstructions,
    notifyOnEdit: data.notifyOnEdit,
    ...buildDonationWriteFields({
      foodTypes: data.foodTypes,
      isPartialRescue: false,
      receiverTeamId:
        data.receiver && data.receiver !== 'unassigned' ? data.receiver : null,
      destAddress: { ...data.dest_address, place: data.dest_address?.name },
    }),
    isCompleted,
    timezone_id: data.timezone_id,
    pickup_address: {
      ...data.pickup_address,
      place: data.pickup_address?.name,
    },
    estimatedRescueTimeMinutes: data.estimatedRescueTimeMinutes,
    aiEnabled: !!data.aiEnabled,
    aiAnalysisResult: data.aiAnalysisResult || null,
    additionalImages: data.additionalImages ?? [],
  };
  return newFoodRun;
};
